/**
 * Pedido de Nova Organização (spec 2026-10-27, §5.3) — `pedido_de_organizacao_routes.py`.
 *
 * Quem tem `organizacao:pedir_nova` pede pelo botão "+ Nova Organização"; o pedido vira chamado
 * no tenant de quem pede, atendido pela DEVOPS. As recusas vêm como
 * `{detail: {codigo, motivo}}`: a tela mostra o `motivo`, que já vem em pt-BR.
 */
import { api } from '@/api/client'
import { formatApiError } from '@/api/errors'

export type TipoDePedido = 'projeto_novo' | 'conta_existente' | 'sem_aws'
export type AcaoDoBotao = 'criar' | 'pedir' | null

export const ROTULO_DO_TIPO: Record<TipoDePedido, string> = {
  projeto_novo: 'Projeto novo',
  conta_existente: 'Projeto que já existe na AWS',
  sem_aws: 'Sem AWS (WhatsApi, KB)',
}

export const ROTULO_DO_ESTADO: Record<string, string> = {
  pendente: 'Pendente',
  recusado: 'Recusado',
  aguardando_reaprovacao: 'Aguardando nova aprovação',
  criando: 'Criando',
  falhou: 'Falhou',
  cancelando: 'Cancelando',
  cancelado: 'Cancelado',
  tramitacao_pendente: 'Tramitação pendente',
  concluido: 'Concluído',
}

export interface Disponibilidade {
  acao: AcaoDoBotao
  tipos: TipoDePedido[]
  motivo: string | null
}

export interface Recusa {
  codigo: string
  motivo: string
}

export type EstadoDoGrupoGitlab =
  | 'visivel_com_maintainer' | 'visivel_sem_acesso' | 'nao_visivel' | 'nao_conferido'

export interface Prontidao {
  tipo: TipoDePedido
  nome: string
  organizacao_slug: string
  project_slug: string
  project_name: string
  pode_enviar: boolean
  recusas: Recusa[]
  conferencias: { item: string; ok: boolean; motivo: string }[]
  /** Não impede o envio: avisa o que depende de outra pessoa (o grupo GitLab). */
  depende_de_terceiro: { item: string; estado: EstadoDoGrupoGitlab | string; motivo: string; acao: string | null }[]
}

export interface ConsultaDeProntidao {
  tipo: TipoDePedido
  nome: string
  project_slug: string
}

export interface CorpoDoPedido extends ConsultaDeProntidao {
  responsavel_user_id: number | null
  justificativa: string
}

export interface PedidoAberto {
  pedido_id: number
  task_request_id: number
  protocolo: string
}

export interface Pessoa {
  id: number
  nome: string
}

export interface PedidoDoChamado {
  pedido: {
    id: number
    tipo: TipoDePedido
    estado: string
    nome: string
    project_slug: string | null
    project_name: string | null
    justificativa: string
    solicitante: Pessoa
    responsavel: Pessoa
    motivo_decisao: string | null
    decidido_por: Pessoa | null
    decidido_em: string | null
    criado_em: string
  }
  pode_decidir: boolean
  pode_cancelar: boolean
}

const base = (tenantId: number) => `/v1/tenants/${tenantId}/pedidos-de-organizacao`

export const chaveDaDisponibilidade = (tenantId: number | null) =>
  ['pedido-org-disponibilidade', tenantId] as const

export const chaveDoPedidoDoChamado = (taskRequestId: number) =>
  ['pedido-de-organizacao', taskRequestId] as const

export const lerDisponibilidade = (tenantId: number): Promise<Disponibilidade> =>
  api.get(`${base(tenantId)}/disponibilidade`).then((r) => r.data)

/** Só leitura: o que o envio conferiria agora, sem gravar nada. */
export const lerProntidao = (tenantId: number, consulta: ConsultaDeProntidao): Promise<Prontidao> =>
  api.get(`${base(tenantId)}/prontidao`, { params: consulta }).then((r) => r.data)

export const abrirPedido = (tenantId: number, corpo: CorpoDoPedido): Promise<PedidoAberto> =>
  api.post(base(tenantId), corpo).then((r) => r.data)

/** 404 quando o chamado não tem pedido, ou quem lê não pode: o painel some. */
export const lerPedidoDoChamado = (taskRequestId: number): Promise<PedidoDoChamado> =>
  api.get(`/v1/chamados/${taskRequestId}/pedido-de-organizacao`).then((r) => r.data)

export const recusarPedido = (
  taskRequestId: number, motivo: string,
): Promise<{ estado: 'recusado'; status_do_chamado: 'rejected' }> =>
  api.post(`/v1/chamados/${taskRequestId}/pedido-de-organizacao/recusar`, { motivo }).then((r) => r.data)

/**
 * O texto da recusa: `detail.motivo` das rotas do pedido, `detail.mensagem` das rotas de
 * organização de hoje (o "Criar" passa por elas), e o resto pelo `formatApiError`.
 */
export function motivoDaRecusa(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } } | null)?.response?.data?.detail
  if (detail && typeof detail === 'object') {
    const { motivo, mensagem } = detail as { motivo?: unknown; mensagem?: unknown }
    if (typeof motivo === 'string' && motivo) return motivo
    if (typeof mensagem === 'string' && mensagem) return mensagem
  }
  return formatApiError(err, fallback)
}
